import React, { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { apiService, MonthlyEntry, PaymentInfo, Receipt } from '../services/api'
import { useOverlay } from '../context/OverlayContext'
import { money, fmtDate, fmtDateTime, monthRange, nowLocalInput, PayerPill, Avatar, Spinner } from '../lib/ui'

interface Props {
  monthId: string | null
  onClose: () => void
}

interface PModal {
  open: boolean
  payId: number | null
  payer: 'David' | 'Timna'
  amount: string
  date: string
  notes: string
}

const emptyModal: PModal = { open: false, payId: null, payer: 'David', amount: '', date: '', notes: '' }

const MonthDetailOverlay: React.FC<Props> = ({ monthId, onClose }) => {
  const queryClient = useQueryClient()
  const { openReceipt } = useOverlay()
  const [shown, setShown] = useState<string | null>(null)
  const [pm, setPm] = useState<PModal>(emptyModal)
  const active = monthId !== null

  useEffect(() => {
    if (monthId !== null) {
      setShown(monthId)
      setPm(emptyModal)
    } else {
      const t = setTimeout(() => setShown(null), 200)
      return () => clearTimeout(t)
    }
  }, [monthId])

  const year = shown ? parseInt(shown.slice(0, 4)) : new Date().getFullYear()

  const { data: monthly } = useQuery({
    queryKey: ['monthly-report', year],
    queryFn: () => apiService.getMonthlyReport(year),
    enabled: shown !== null,
  })

  const { data: receipts = [] } = useQuery({
    queryKey: ['receipts', 'month', shown],
    queryFn: () => apiService.getReceipts({ ...monthRange(shown as string), limit: 500 }),
    enabled: shown !== null,
  })

  const entry: MonthlyEntry | undefined = monthly?.find((m) => m.month_id === shown)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['monthly-report'] })
    queryClient.invalidateQueries({ queryKey: ['summary'] })
    queryClient.invalidateQueries({ queryKey: ['payments'] })
  }

  const createPayment = useMutation({
    mutationFn: apiService.createPayment,
    onSuccess: () => { toast.success('Payment added'); invalidate(); setPm(emptyModal) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Failed to add payment'),
  })
  const updatePayment = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiService.updatePayment(id, data),
    onSuccess: () => { toast.success('Payment updated'); invalidate(); setPm(emptyModal) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Failed to update payment'),
  })
  const deletePayment = useMutation({
    mutationFn: (id: number) => apiService.deletePayment(id),
    onSuccess: () => { toast.success('Payment deleted'); invalidate(); setPm(emptyModal) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Failed to delete payment'),
  })

  const onBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement
    if (t.classList.contains('overlay') || t.classList.contains('panel')) onClose()
  }

  const openAdd = () => setPm({ open: true, payId: null, payer: 'David', amount: '', date: nowLocalInput(), notes: '' })
  const openEdit = (p: PaymentInfo) => setPm({ open: true, payId: p.id, payer: p.payer as 'David' | 'Timna', amount: String(p.amount), date: p.payment_date.slice(0, 16), notes: p.notes || '' })

  const savePayment = () => {
    const amount = parseFloat(pm.amount)
    if (!amount) { toast.error('Enter an amount'); return }
    if (pm.payId) {
      updatePayment.mutate({ id: pm.payId, data: { month_id: shown, payer: pm.payer, amount, payment_date: pm.date, notes: pm.notes } })
    } else {
      createPayment.mutate({ month_id: shown as string, payer: pm.payer, amount, payment_date: pm.date, notes: pm.notes })
    }
  }

  const overUnder = (paid: number, should: number) =>
    paid > should + 0.01
      ? <span className="tiny" style={{ color: 'var(--david)' }}>overpaid</span>
      : paid < should - 0.01
        ? <span className="tiny" style={{ color: 'var(--text-2)' }}>underpaid</span>
        : <span className="tiny muted">exact</span>

  const settled = entry?.who_owes === 'Nobody'
  const sortedReceipts = [...receipts].sort((a, b) => b.date.localeCompare(a.date))
  const nextNum = pm.payId
    ? (entry?.payments.find((p) => p.id === pm.payId)?.payment_number ?? 1)
    : (Math.max(0, ...(entry?.payments.map((p) => p.payment_number) || [])) + 1)

  return (
    <>
      <div className={`overlay${active ? ' active' : ''}`} onClick={onBackdrop}>
        <div className="panel">
          {shown !== null && (
            <>
              <div className="panel-head"><div className="back" onClick={onClose}>← Balance</div></div>

              {!entry ? (
                <div className="glass-card"><div className="center-pad"><Spinner /></div></div>
              ) : (
                <>
                  <div className="hero glass" style={{ padding: '26px 22px' }}>
                    <div className="eyebrow">{entry.month_name}</div>
                    {settled ? (
                      <>
                        <div className="amount" style={{ fontSize: 36 }}>Settled</div>
                        <div className="who">Everything's square for this month</div>
                      </>
                    ) : (
                      <>
                        <div className="amount" style={{ fontSize: 40 }}>{money(entry.remaining_balance)}</div>
                        <div className="who"><b>{entry.who_owes}</b> still owes</div>
                      </>
                    )}
                    <div style={{ marginTop: 6 }} className="tiny muted">Total spent {money(entry.david_paid + entry.timna_paid)}</div>
                  </div>

                  <div className="split-row">
                    <div className="who-card glass">
                      <Avatar who="David" />
                      <div className="tiny muted">David</div>
                      <div className="val" style={{ color: 'var(--david)' }}>{money(entry.david_paid)}</div>
                      <div className="tiny muted">should pay {money(entry.david_should_pay)}</div>
                      {overUnder(entry.david_paid, entry.david_should_pay)}
                    </div>
                    <div className="who-card glass">
                      <Avatar who="Timna" />
                      <div className="tiny muted">Timna</div>
                      <div className="val" style={{ color: 'var(--timna)' }}>{money(entry.timna_paid)}</div>
                      <div className="tiny muted">should pay {money(entry.timna_should_pay)}</div>
                      {overUnder(entry.timna_paid, entry.timna_should_pay)}
                    </div>
                  </div>

                  <div className="section-title" style={{ justifyContent: 'space-between' }}>
                    <span>Payment tickets</span>
                    <button className="btn-sm" onClick={openAdd}>Add payment</button>
                  </div>
                  <div>
                    {entry.payments.length ? entry.payments.map((p) => (
                      <div key={p.id} className="row glass" style={{ cursor: 'default' }}>
                        <div className="row-left">
                          <div className="ico">P</div>
                          <div>
                            <div className="row-title"><span className="pill pill-track">{p.tracking_number}</span></div>
                            <div className="row-sub">{p.payer} paid · {fmtDate(p.payment_date)}{p.notes ? ` · ${p.notes}` : ''}</div>
                          </div>
                        </div>
                        <div className="row-right">
                          <span className="amt" style={{ color: 'var(--david)' }}>{money(p.amount)}</span>
                          <span>
                            <button className="btn-sm" onClick={() => openEdit(p)}>Edit</button>{' '}
                            <button className="btn-sm red" onClick={() => { if (window.confirm('Delete this payment?')) deletePayment.mutate(p.id) }}>Del</button>
                          </span>
                        </div>
                      </div>
                    )) : <div className="note note-info">No payments recorded for {entry.month_name} yet.</div>}
                  </div>

                  <div className="section-title">Receipts this month <span className="tiny muted" style={{ fontWeight: 400 }}>— tap for full detail</span></div>
                  <div>
                    {sortedReceipts.map((r: Receipt) => (
                      <div key={r.id} className="row glass" onClick={() => openReceipt(r.id)}>
                        <div className="row-left">
                          <div className="ico">{r.company.charAt(0).toUpperCase()}</div>
                          <div>
                            <div className="row-title">{r.company}</div>
                            <div className="row-sub">{fmtDateTime(r.date)}{r.notes ? ` · “${r.notes}”` : ''}</div>
                          </div>
                        </div>
                        <div className="row-right">
                          <span className="amt">{money(r.total_amount)}</span>
                          <span>
                            <PayerPill payer={r.payer} />{' '}
                            <span className={`pill pill-${r.is_shared ? 'shared' : 'personal'}`}>{r.is_shared ? 'Shared' : 'Personal'}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* payment modal */}
      <div className={`modal-wrap${pm.open ? ' active' : ''}`} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('modal-wrap')) setPm(emptyModal) }}>
        <div className="modal glass">
          <h3>{pm.payId ? 'Edit' : 'Add'} payment <span className="x" onClick={() => setPm(emptyModal)}>×</span></h3>
          <div className="field" style={{ marginTop: 0 }}>
            <label>Ticket #</label>
            <div><span className="pill pill-track">T{shown}-P{nextNum}</span></div>
          </div>
          <div className="field">
            <label>Who is paying?</label>
            <div className="radios">
              <div className={`radio${pm.payer === 'David' ? ' on' : ''}`} onClick={() => setPm({ ...pm, payer: 'David' })}>David</div>
              <div className={`radio${pm.payer === 'Timna' ? ' on' : ''}`} onClick={() => setPm({ ...pm, payer: 'Timna' })}>Timna</div>
            </div>
          </div>
          <div className="field"><label>Amount (€)</label><input type="number" step="0.01" value={pm.amount} placeholder="0.00" onChange={(e) => setPm({ ...pm, amount: e.target.value })} /></div>
          <div className="field"><label>Date</label><input type="datetime-local" value={pm.date} onChange={(e) => setPm({ ...pm, date: e.target.value })} /></div>
          <div className="field"><label>Notes</label><input value={pm.notes} placeholder="optional" onChange={(e) => setPm({ ...pm, notes: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            {pm.payId ? <button className="cta danger" onClick={() => { if (pm.payId && window.confirm('Delete this payment?')) deletePayment.mutate(pm.payId) }}>Delete</button> : null}
            <button className="cta" style={{ flex: 1, justifyContent: 'center' }} disabled={createPayment.isLoading || updatePayment.isLoading} onClick={savePayment}>
              {pm.payId ? 'Update' : 'Add payment'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default MonthDetailOverlay
